import React, { useEffect, useState, useRef } from "react";

// Shared helper functions
const getKeyResponsibilities = (teamMember) => {
  if (teamMember?.key_responsibilities && teamMember.key_responsibilities.length > 0) {
    return teamMember.key_responsibilities;
  }
  if (teamMember?.activities_skills && teamMember.activities_skills.length > 0) {
    return teamMember.activities_skills.map(item => item.heading || item.desc);
  }
  return [
    "Strategic leadership and vision setting",
    "Team development and mentorship",
    "Stakeholder engagement and partnerships",
    "Innovation and process improvement"
  ];
};

const getAreasOfFocus = (teamMember) => {
  if (teamMember?.areas_of_focus && teamMember.areas_of_focus.length > 0) {
    return teamMember.areas_of_focus;
  }
  if (teamMember?.professional_skills && teamMember.professional_skills.length > 0) {
    return teamMember.professional_skills.map(skill => skill.heading);
  }
  const roleBasedTags = {
    "CEO": ["Strategic Planning", "Leadership", "Innovation", "Growth"],
    "Director": ["Operations", "Strategy", "Management", "Execution"],
    "Chairperson": ["Governance", "Strategy", "Advisory", "Leadership"],
    "Advisor": ["Consulting", "Expertise", "Guidance", "Strategy"]
  };
  return roleBasedTags[teamMember?.role] || ["Leadership", "Strategy", "Innovation"];
};

const getHighlights = (teamMember) => {
  if (teamMember?.highlights && teamMember.highlights.length > 0) {
    return teamMember.highlights;
  }
  if (teamMember?.key_contributions && teamMember.key_contributions.length > 0) {
    return teamMember.key_contributions;
  }
  return null;
};

const getShortBio = (teamMember) => {
  if (teamMember?.about) {
    return teamMember.about;
  }
  if (teamMember?.short_bio) {
    return teamMember.short_bio;
  }
  if (teamMember?.description) {
    const sentences = teamMember.description.split(/[.!?]+/).filter(s => s.trim().length > 0);
    return sentences.slice(0, 3).join(". ") + ".";
  }
  return "Experienced leader with a proven track record in strategic planning and team development. Passionate about innovation and driving organizational growth through collaborative leadership.";
};

const getOneLineIntro = (teamMember) => {
  if (teamMember?.one_line_intro) {
    return teamMember.one_line_intro;
  }
  if (teamMember?.tagline) {
    return teamMember.tagline;
  }
  const role = teamMember?.role || "Team Member";
  return `${role} dedicated to driving excellence and innovation.`;
};

// Hook for scroll animations
const useScrollAnimation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Variation 1: Animated Hero with Floating Cards
export const TeamDetailAnimatedHero = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);
  const [ref1, visible1] = useScrollAnimation();
  const [ref2, visible2] = useScrollAnimation();
  const [ref3, visible3] = useScrollAnimation();

  return (
    <div className="team-detail-animated-hero">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        /* D-Right brand tokens (fallbacks included) */
        :root {
          --dright-green: var(--brand-green-primary, #4E8F5A);
          --dright-green-dark: var(--brand-green-dark, #1F3D2B);
          --dright-green-hover: var(--brand-green-hover, #3F764A);
        }
        .fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }
        .slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }
        .scale-in {
          animation: scaleIn 0.6s ease-out forwards;
        }
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
        .gradient-animated {
          /* D-Right green gradient */
          background: linear-gradient(-45deg, #1F3D2B, #4E8F5A, #2C5F3D, #3F764A);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
        }
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }
        /* CTA Buttons Mobile Styles */
        @media (max-width: 768px) {
          .cta-buttons-wrapper {
            flex-direction: column !important;
            width: 100% !important;
            gap: 1rem !important;
          }
          .cta-button-primary,
          .cta-button-secondary {
            width: 100% !important;
            min-width: unset !important;
            max-width: 100% !important;
            padding: 1rem 2rem !important;
            font-size: 1rem !important;
          }
        }
        @media (max-width: 480px) {
          .cta-button-primary,
          .cta-button-secondary {
            padding: 0.875rem 1.5rem !important;
            font-size: 0.9375rem !important;
            height: 52px !important;
          }
        }
        @media (hover: none) and (pointer: coarse) {
          .cta-button-primary:active,
          .cta-button-secondary:active {
            transform: translateY(-2px) !important;
          }
        }
      `}</style>

      {/* Animated Hero Section */}
      <section className="gradient-animated" style={{ 
        paddingTop: "3rem", 
        paddingBottom: "2.5rem",
        position: "relative",
        overflow: "hidden",
        zIndex: 0
      }}>
        <div className="container position-relative" style={{ zIndex: 10 }}>
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center fade-in-up" style={{ position: "relative", zIndex: 10 }}>
              <div className="float-animation" style={{
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "8px solid #1F3D2B",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                margin: "0 auto 1.5rem",
                position: "relative",
                zIndex: 11
              }}>
                <div style={{
                  position: "absolute",
                  top: "-10px",
                  right: "-10px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  background: "#1F3D2B",
                  backdropFilter: "blur(10px)",
                  border: "2px solid #2C5F3D",
                  animation: "float 4s ease-in-out infinite",
                  animationDelay: "1s",
                  zIndex: 12
                }}></div>
              </div>
              <h1 style={{ 
                fontSize: "2.5rem", 
                fontWeight: "800", 
                color: "#ffffff", 
                marginBottom: "0.75rem",
                textShadow: "0 4px 20px rgba(0,0,0,0.2)",
                background: "transparent",
                padding: "0",
                border: "none",
                display: "inline-block",
                position: "relative",
                zIndex: 11
              }}>
                {teamMember?.name || "Team Member"}
              </h1>
              <p style={{ 
                fontSize: "1.25rem", 
                color: "rgba(255,255,255,0.95)", 
                marginBottom: "1.5rem",
                fontWeight: "600",
                position: "relative",
                zIndex: 11
              }}>
                {teamMember?.role || "Role"}
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "1rem" }}>
                {teamMember?.social?.linkedin && (
                  <a href={teamMember.social.linkedin} target="_blank" rel="noopener noreferrer"
                    className="scale-in"
                    style={{ 
                      width: "56px", 
                      height: "56px", 
                      borderRadius: "50%", 
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      color: "#fff", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      textDecoration: "none",
                      fontSize: "1.5rem",
                      border: "2px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.3)";
                      e.target.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.2)";
                      e.target.style.transform = "scale(1)";
                    }}
                  >
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                )}
                {teamMember?.contact?.email && (
                  <a href={`mailto:${teamMember.contact.email}`}
                    className="scale-in"
                    style={{ 
                      width: "56px", 
                      height: "56px", 
                      borderRadius: "50%", 
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      color: "#fff", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center", 
                      textDecoration: "none",
                      fontSize: "1.5rem",
                      border: "2px solid rgba(255,255,255,0.3)",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.3)";
                      e.target.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "rgba(255,255,255,0.2)";
                      e.target.style.transform = "scale(1)";
                    }}
                  >
                    <i className="fas fa-envelope"></i>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Content Sections */}
      <section style={{ background: "#f7fafc", paddingTop: "4rem", paddingBottom: "4rem" }}>
        <div className="container">
          {/* Bio Card */}
          <div ref={ref1} className={visible1 ? "fade-in-up" : ""} style={{ opacity: visible1 ? 1 : 0 }}>
            <div className="card-hover" style={{
              background: "#ffffff",
              borderRadius: "24px",
              padding: "3rem",
              marginBottom: "2rem",
              boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
              border: "1px solid rgba(78,143,90,0.1)"
            }}>
              <h2 style={{ fontSize: "2rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>About</h2>
              <p style={{ fontSize: "1.125rem", color: "#4a5568", lineHeight: "1.9", margin: "0" }}>{shortBio}</p>
            </div>
          </div>

          {/* Two Column Cards */}
          <div className="row">
            <div className="col-md-6 mb-4">
              <div ref={ref2} className={visible2 ? "slide-in-left" : ""} style={{ opacity: visible2 ? 1 : 0 }}>
                <div className="card-hover" style={{
                  background: "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)",
                  borderRadius: "20px",
                  padding: "2.5rem",
                  height: "100%",
                  boxShadow: "0 8px 30px rgba(78,143,90,0.3)",
                  border: "1px solid rgba(78,143,90,0.2)"
                }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.2)",
                    backdropFilter: "blur(10px)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1.5rem",
                    fontSize: "1.5rem",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)"
                  }}>
                    <i className="fas fa-tasks"></i>
                  </div>
                  <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#ffffff", marginBottom: "1.5rem" }}>Key Responsibilities</h3>
                  <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                    {keyResponsibilities.slice(0, 6).map((item, index) => (
                      <li key={index} style={{
                        padding: "0.875rem 0",
                        paddingLeft: "2rem",
                        position: "relative",
                        fontSize: "1rem",
                        color: "#ffffff",
                        lineHeight: "1.8",
                        transition: "all 0.3s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.paddingLeft = "2.5rem";
                        e.target.style.color = "#AEE5C2";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.paddingLeft = "2rem";
                        e.target.style.color = "#ffffff";
                      }}
                      >
                        <span style={{
                          position: "absolute",
                          left: "0",
                          top: "1.125rem",
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: "rgba(255,255,255,0.8)",
                          boxShadow: "0 2px 8px rgba(255,255,255,0.3)"
                        }}></span>
                        {typeof item === 'string' ? item : (item.heading || item.desc)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div ref={ref3} className={visible3 ? "slide-in-right" : ""} style={{ opacity: visible3 ? 1 : 0 }}>
                <div className="card-hover" style={{
                  background: "#ffffff",
                  borderRadius: "20px",
                  padding: "2.5rem",
                  height: "100%",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(78,143,90,0.1)"
                }}>
                  <div style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1.5rem",
                    fontSize: "1.5rem",
                    boxShadow: "0 8px 20px rgba(78,143,90,0.3)"
                  }}>
                    <i className="fas fa-bullseye"></i>
                  </div>
                  <h3 style={{ fontSize: "1.5rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Areas of Focus</h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.875rem" }}>
                    {areasOfFocus.map((area, index) => (
                      <span
                        key={index}
                        style={{
                          padding: "0.75rem 1.5rem",
                          background: "linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)",
                          color: "#2d3748",
                          borderRadius: "25px",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          border: "2px solid transparent",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)";
                          e.target.style.color = "#fff";
                          e.target.style.transform = "scale(1.05)";
                          e.target.style.boxShadow = "0 4px 12px rgba(78,143,90,0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)";
                          e.target.style.color = "#2d3748";
                          e.target.style.transform = "scale(1)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Highlights */}
          {highlights && highlights.length > 0 && (
            <div className="row justify-content-center mt-4">
              <div className="col-lg-10">
                <div className="card-hover" style={{
                  background: "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)",
                  borderRadius: "20px",
                  padding: "3rem",
                  boxShadow: "0 12px 40px rgba(78,143,90,0.3)"
                }}>
                  <h3 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#ffffff", marginBottom: "2rem", textAlign: "center" }}>Key Contributions</h3>
                  <div className="row">
                    {highlights.slice(0, 3).map((highlight, index) => (
                      <div key={index} className="col-md-4 mb-3 mb-md-0">
                        <div style={{
                          background: "rgba(255,255,255,0.15)",
                          backdropFilter: "blur(10px)",
                          borderRadius: "16px",
                          padding: "1.5rem",
                          height: "100%",
                          border: "1px solid rgba(255,255,255,0.2)",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "rgba(255,255,255,0.25)";
                          e.target.style.transform = "translateY(-5px)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "rgba(255,255,255,0.15)";
                          e.target.style.transform = "translateY(0)";
                        }}
                        >
                          <div style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            background: "rgba(255,255,255,0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1rem",
                            fontSize: "1.25rem",
                            color: "#fff"
                          }}>
                            <i className="fas fa-star"></i>
                          </div>
                          <p style={{ fontSize: "1rem", color: "#ffffff", lineHeight: "1.7", margin: "0" }}>
                            {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

// Variation 2: Glassmorphism with Parallax
export const TeamDetailGlassParallax = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="team-detail-glass-parallax">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>

      {/* Parallax Hero */}
      <section style={{
        background: `linear-gradient(135deg, #1F3D2B 0%, #4E8F5A 50%, #3F764A 100%)`,
        paddingTop: "3.5rem",
        paddingBottom: "3rem",
        position: "relative",
        overflow: "hidden",
        zIndex: 0
      }}>
        <div style={{
          position: "absolute",
          top: `${30 + scrollY * 0.3}px`,
          left: "50%",
          transform: "translateX(-50%)",
          width: "240px",
          height: "240px",
          borderRadius: "50%",
          background: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          border: "10px solid #1F3D2B",
          boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
          zIndex: 10
        }} />
        <div className="container position-relative" style={{ zIndex: 11, marginTop: "280px" }}>
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center" style={{ position: "relative", zIndex: 11 }}>
              <h1 style={{
                fontSize: "2.75rem",
                fontWeight: "800",
                color: "#ffffff",
                marginBottom: "0.75rem",
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background: "transparent",
                padding: "0",
                border: "none",
                display: "inline-block",
                position: "relative",
                zIndex: 12
              }}>
                {teamMember?.name || "Team Member"}
              </h1>
              <p style={{
                fontSize: "1.5rem",
                color: "rgba(255,255,255,0.95)",
                marginBottom: "0",
                fontWeight: "600",
                position: "relative",
                zIndex: 12
              }}>
                {teamMember?.role || "Role"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Glass Cards */}
      <section style={{
        background: "linear-gradient(180deg, #4E8F5A 0%, #f7fafc 50%)",
        paddingTop: "4rem",
        paddingBottom: "4rem",
        marginTop: "-100px"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Bio Glass Card */}
              <div className="glass-card shimmer" style={{
                borderRadius: "24px",
                padding: "3rem",
                marginBottom: "2rem",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
              }}>
                <h2 style={{ fontSize: "2rem", fontWeight: "700", color: "#ffffff", marginBottom: "1.5rem" }}>About</h2>
                <p style={{ fontSize: "1.125rem", color: "rgba(255,255,255,0.95)", lineHeight: "1.9", margin: "0" }}>{shortBio}</p>
              </div>

              {/* Grid Cards */}
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div className="glass-card" style={{
                    borderRadius: "20px",
                    padding: "2.5rem",
                    height: "100%",
                    boxShadow: "0 15px 50px rgba(0,0,0,0.15)"
                  }}>
                    <h3 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#ffffff", marginBottom: "1.5rem" }}>Key Responsibilities</h3>
                    <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                      {keyResponsibilities.slice(0, 6).map((item, index) => (
                        <li key={index} style={{
                          padding: "0.875rem 0",
                          paddingLeft: "2rem",
                          position: "relative",
                          fontSize: "1rem",
                          color: "#ffffff",
                          lineHeight: "1.8"
                        }}>
                          <span style={{
                            position: "absolute",
                            left: "0",
                            top: "1.125rem",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.6)",
                            boxShadow: "0 0 10px rgba(255,255,255,0.5)"
                          }}></span>
                          {typeof item === 'string' ? item : (item.heading || item.desc)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="col-md-6 mb-4">
                  <div className="glass-card" style={{
                    borderRadius: "20px",
                    padding: "2.5rem",
                    height: "100%",
                    boxShadow: "0 15px 50px rgba(0,0,0,0.15)"
                  }}>
                    <h3 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#ffffff", marginBottom: "1.5rem" }}>Areas of Focus</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.875rem" }}>
                      {areasOfFocus.map((area, index) => (
                        <span
                          key={index}
                          style={{
                            padding: "0.75rem 1.5rem",
                            background: "rgba(255,255,255,0.2)",
                            backdropFilter: "blur(10px)",
                            color: "#fff",
                            borderRadius: "25px",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            border: "1px solid rgba(255,255,255,0.3)",
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "rgba(255,255,255,0.4)";
                            e.target.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "rgba(255,255,255,0.2)";
                            e.target.style.transform = "scale(1)";
                          }}
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Variation 3: 3D Card Tilt Effect
export const TeamDetail3DCards = ({ teamMember }) => {
  const keyResponsibilities = getKeyResponsibilities(teamMember);
  const areasOfFocus = getAreasOfFocus(teamMember);
  const highlights = getHighlights(teamMember);
  const shortBio = getShortBio(teamMember);
  const oneLineIntro = getOneLineIntro(teamMember);

  const handleMouseMove = (e, card) => {
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;
    
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
  };

  const handleMouseLeave = (card) => {
    card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
  };

  return (
    <div className="team-detail-3d-cards">
      <style>{`
        .card-3d {
          transition: transform 0.1s ease-out;
          transform-style: preserve-3d;
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(78,143,90,0.3); }
          50% { box-shadow: 0 0 40px rgba(78,143,90,0.6); }
        }
        .glow-animation {
          animation: glow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Hero */}
      <section style={{
        background: "linear-gradient(135deg, #1F3D2B 0%, #4E8F5A 50%, #3F764A 100%)",
        paddingTop: "3rem",
        paddingBottom: "2.5rem",
        position: "relative",
        overflow: "hidden",
        zIndex: 0
      }}>
        <div className="container" style={{ position: "relative", zIndex: 10 }}>
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center" style={{ position: "relative", zIndex: 10 }}>
              <div className="glow-animation" style={{
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "6px solid #1F3D2B",
                margin: "0 auto 1.5rem",
                position: "relative",
                zIndex: 11
              }}>
                <div style={{
                  position: "absolute",
                  inset: "-10px",
                  borderRadius: "50%",
                  border: "3px solid #2C5F3D",
                  opacity: 0.5,
                  animation: "glow 2s ease-in-out infinite",
                  zIndex: 10
                }}></div>
              </div>
              <h1 style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                color: "#ffffff",
                marginBottom: "0.75rem",
                background: "transparent",
                padding: "0",
                border: "none",
                display: "inline-block",
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                position: "relative",
                zIndex: 11
              }}>
                {teamMember?.name || "Team Member"}
              </h1>
              <p style={{
                fontSize: "1.25rem",
                color: "#ffffff",
                fontWeight: "600",
                marginBottom: "0",
                position: "relative",
                zIndex: 11
              }}>
                {teamMember?.role || "Role"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3D Cards */}
      <section style={{ background: "#f7fafc", paddingTop: "4rem", paddingBottom: "4rem" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Bio Card */}
              <div
                className="card-3d"
                ref={(el) => {
                  if (el) {
                    el.addEventListener('mousemove', (e) => handleMouseMove(e, el));
                    el.addEventListener('mouseleave', () => handleMouseLeave(el));
                  }
                }}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
                  borderRadius: "24px",
                  padding: "3rem",
                  marginBottom: "2rem",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(78,143,90,0.1)"
                }}
              >
                <h2 style={{ fontSize: "2rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>About</h2>
                <p style={{ fontSize: "1.125rem", color: "#4a5568", lineHeight: "1.9", margin: "0" }}>{shortBio}</p>
              </div>

              {/* Grid */}
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div
                    className="card-3d"
                    ref={(el) => {
                      if (el) {
                        el.addEventListener('mousemove', (e) => handleMouseMove(e, el));
                        el.addEventListener('mouseleave', () => handleMouseLeave(el));
                      }
                    }}
                    style={{
                      background: "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)",
                      borderRadius: "20px",
                      padding: "2.5rem",
                      height: "100%",
                      boxShadow: "0 15px 50px rgba(78,143,90,0.3)",
                      color: "#fff"
                    }}
                  >
                    <h3 style={{ fontSize: "1.75rem", fontWeight: "700", marginBottom: "1.5rem" }}>Key Responsibilities</h3>
                    <ul style={{ listStyle: "none", padding: "0", margin: "0" }}>
                      {keyResponsibilities.slice(0, 6).map((item, index) => (
                        <li key={index} style={{
                          padding: "0.875rem 0",
                          paddingLeft: "2rem",
                          position: "relative",
                          fontSize: "1rem",
                          color: "#ffffff",
                          lineHeight: "1.8"
                        }}>
                          <span style={{
                            position: "absolute",
                            left: "0",
                            top: "1.125rem",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.8)"
                          }}></span>
                          {typeof item === 'string' ? item : (item.heading || item.desc)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="col-md-6 mb-4">
                  <div
                    className="card-3d"
                    ref={(el) => {
                      if (el) {
                        el.addEventListener('mousemove', (e) => handleMouseMove(e, el));
                        el.addEventListener('mouseleave', () => handleMouseLeave(el));
                      }
                    }}
                    style={{
                      background: "linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)",
                      borderRadius: "20px",
                      padding: "2.5rem",
                      height: "100%",
                      boxShadow: "0 15px 50px rgba(0,0,0,0.1)",
                      border: "1px solid rgba(78,143,90,0.1)"
                    }}
                  >
                    <h3 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#1a202c", marginBottom: "1.5rem" }}>Areas of Focus</h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.875rem" }}>
                      {areasOfFocus.map((area, index) => (
                        <span
                          key={index}
                          style={{
                            padding: "0.75rem 1.5rem",
                            background: "linear-gradient(135deg, #4E8F5A 0%, #3F764A 100%)",
                            color: "#fff",
                            borderRadius: "25px",
                            fontSize: "0.95rem",
                            fontWeight: "600",
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "scale(1.1) rotate(2deg)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "scale(1) rotate(0deg)";
                          }}
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
